import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Common } from '@thecvlb/design-system';
import classNames from 'classnames';
import dayjs from 'dayjs';

import { useUploadFileMutation } from 'services/channels/channels';
import { UploadFileDataProps, UploadFileResProps } from 'services/channels/channels.types';
import { useGetGlobalMessagesQuery } from 'services/general/general';

import { selectUser, selectWriteMessageFeatureStatus } from 'store';
import { setModal } from 'store/modals/modalSlice';

import FadeWrapper from 'shared/animationWrappers/FadeWrapper';
import Loader from 'shared/Loader';
import { notifyError } from 'shared/Toast/Toast';
import ChatDate from 'widgets/Chat/ChatDate';
import ChatForm from 'widgets/Chat/ChatForm';
import { fileValidator } from 'widgets/Chat/ChatForm/chatForm.settings';
import { ChatFormAttachment } from 'widgets/Chat/ChatForm/chatForm.types';
import Message from 'widgets/Chat/Message';

import { DateMessagesProps, UserTypeProps } from 'contexts/messagesContext/messagesContext.types';
import { useAppDispatch, useAppSelector, useFrontDesk, useMessages, useQuery } from 'hooks';
import { hasPaymentMethodOnFile } from 'hooks/usePaywall/eligibilityAccess';
import { DEFAULT_MESSAGE } from 'utils/constants';
import { fileToDataUrl } from 'utils/file';
import { handleRequestCatch } from 'utils/error';

import { ChatProps } from './chat.types';

type CombinedMessage = Pick<
  DateMessagesProps['messages'][number],
  'messageStatus' | 'senderDetails'
>;

type ChatDraftAttachment = ChatFormAttachment & {
  file: File;
};

type ChatDraftEntry = {
  attachments: ChatDraftAttachment[];
  message: string;
};

const EMPTY_CHAT_DRAFT: ChatDraftEntry = {
  attachments: [],
  message: ''
};

const getCombinedMessageState = (
  currentMessage: CombinedMessage,
  nextMessage?: CombinedMessage
) => {
  if (!nextMessage) {
    return false;
  }

  const minutes = dayjs(nextMessage.messageStatus.sent).diff(
    currentMessage.messageStatus.sent,
    'minutes'
  );

  return nextMessage.senderDetails?.id === currentMessage.senderDetails?.id && minutes < 5;
};

const Chat: React.FC<ChatProps> = ({ isZoomCall }) => {
  const dispatch = useAppDispatch();
  const [uploadFile, { isLoading }] = useUploadFileMutation();
  const { data } = useGetGlobalMessagesQuery();

  const {
    channelDetails,
    channels,
    closeChannel,
    dateMessages,
    generalHealth,
    getOldMessage,
    isLoading: isLoadingMessages,
    joinRoom,
    markSeen,
    sendMessage
  } = useMessages();
  const {
    channelDetails: channelDetailsFrontDesk,
    channels: channelsFrontDesk,
    closeChannel: closeChannelFrontDesk,
    dateMessages: dateMessagesFrontDesk,
    getOldMessage: getOldMessageFrontDesk,
    isLoading: isLoadingFrontDesk,
    joinRoom: joinRoomFrontDesk,
    markSeen: markSeenFrontDesk,
    sendMessage: sendMessageFrontDesk
  } = useFrontDesk();
  const query = useQuery();
  const chatId = isZoomCall ? generalHealth : query.get('chatId');
  const isFrontDesk = !channels.find((el) => el.channelId === chatId);
  const activeDraftKey = chatId ? `${isFrontDesk ? 'frontDesk' : 'careTeam'}:${chatId}` : null;
  const { paymentProfiles, userId, userType } = useAppSelector(selectUser);
  const writeMessagesStatus = useAppSelector(selectWriteMessageFeatureStatus);
  const [skip, setSkip] = useState(1);
  const [scrollTop, setScrollTop] = useState(0);
  const scrollBlock = useRef<HTMLDivElement | null>(null);
  const [disabledSubmit, setDisabledSubmit] = useState(false);
  const [draftsByChannel, setDraftsByChannel] = useState<Record<string, ChatDraftEntry>>({});
  const activeDraft =
    (activeDraftKey ? draftsByChannel[activeDraftKey] : undefined) ?? EMPTY_CHAT_DRAFT;

  const updateActiveDraft = (updater: (current: ChatDraftEntry) => ChatDraftEntry) => {
    if (!activeDraftKey) {
      return;
    }

    setDraftsByChannel((current) => ({
      ...current,
      [activeDraftKey]: updater(current[activeDraftKey] ?? EMPTY_CHAT_DRAFT)
    }));
  };

  const clearActiveDraft = () => {
    if (!activeDraftKey) {
      return;
    }

    setDraftsByChannel((current) => {
      const { [activeDraftKey]: _removed, ...rest } = current;
      return rest;
    });
  };

  const handleDraftChange = (nextMessage: string) => {
    updateActiveDraft((current) => ({
      ...current,
      message: nextMessage
    }));
  };

  const addDraftAttachments = async (files: FileList | File[]) => {
    const selectedFiles = Array.from(files);
    if (!selectedFiles.length) {
      return;
    }

    const firstFile = selectedFiles[0];
    const validationResult = fileValidator({
      0: firstFile,
      item: () => firstFile,
      length: selectedFiles.length,
      [Symbol.iterator]: function* () {
        yield* selectedFiles;
      }
    } as FileList);

    if (!validationResult.isValidFile) {
      if (validationResult.errVal) {
        notifyError(validationResult.errVal);
      }
      return;
    }

    const convertedFiles = await Promise.all(
      selectedFiles.map((file) => fileToDataUrl(file, { convertHeicToJpeg: true }))
    );

    updateActiveDraft((current) => ({
      ...current,
      attachments: [
        ...current.attachments,
        ...convertedFiles.map(({ dataUrl, file }) => ({
          file,
          id: file.name,
          name: file.name,
          previewUrl: dataUrl,
          type: file.type
        }))
      ]
        .filter(
          (attachment, index, attachments) =>
            attachments.findIndex((candidate) => candidate.name === attachment.name) === index
        )
        .slice(0, 10)
    }));
  };

  const removeDraftAttachment = (attachmentId: string) => {
    updateActiveDraft((current) => ({
      ...current,
      attachments: current.attachments.filter((attachment) => attachment.id !== attachmentId)
    }));
  };

  const handleSendMessage = (
    message: string,
    file: UploadFileDataProps | null,
    reset: () => void
  ) => {
    if (writeMessagesStatus?.status === 'paywall') {
      const action = 'action' in writeMessagesStatus ? writeMessagesStatus.action : undefined;
      if (action?.type === 'SHOW_PAYWALL') {
        const reason = action.paywall.code;
        return dispatch(
          setModal(
            reason === 'program_resume'
              ? {
                  key: 'resumeToProgram',
                  value: {
                    programIds: action.paywall.programIds,
                    show: true
                  }
                }
              : {
                  key: 'paymentPastDue',
                  value: {
                    show: true
                  }
                }
          )
        );
      }
      if (!hasPaymentMethodOnFile(paymentProfiles)) {
        return dispatch(
          setModal({
            key: 'paymentPastDue',
            value: {
              show: true
            }
          })
        );
      }
    }
    if (channelDetails || channelDetailsFrontDesk) {
      const data = {
        message,
        ...file
      };
      if (channelDetailsFrontDesk) {
        sendMessageFrontDesk({
          ...data,
          channelId: channelDetailsFrontDesk?._id,
          receiverDetails: channelDetailsFrontDesk?.staffDetail
        });
      }
      if (channelDetails) {
        sendMessage({
          ...data,
          channelId: channelDetails._id,
          receiverDetails: channelDetails.staffs,
          senderDetails: { id: userId, userType: userType as UserTypeProps }
        });
      }
      reset();
      scrollBlock.current?.scrollTo && scrollBlock.current.scrollTo(0, 0);
    }
  };

  const uploadFileForChat = (file: File): Promise<void | UploadFileResProps> => {
    const formData = new FormData();
    formData.append('chatFile', file);
    return uploadFile(formData).unwrap().catch(handleRequestCatch);
  };

  const onSubmit = async () => {
    const message = activeDraft.message;
    const files = activeDraft.attachments.map((attachment) => attachment.file);
    let hasSentMessage = false;

    if (files.length) {
      for (const [index, fileSelected] of files.entries()) {
        setDisabledSubmit(true);
        const file = await uploadFileForChat(fileSelected);
        setDisabledSubmit(false);
        if (!file) {
          continue;
        }
        hasSentMessage = true;
        handleSendMessage(
          files.length - 1 === index ? message.trim() : '',
          file.data,
          () => undefined
        );
      }
    } else {
      hasSentMessage = true;
      handleSendMessage(message.trim(), null, () => undefined);
    }

    if (hasSentMessage) {
      clearActiveDraft();
    }
  };

  const handleScroll = (e: SyntheticEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  };

  const handleJoinRoom = () => {
    if (!isFrontDesk && channels.length && !channelDetails && chatId) {
      joinRoom(chatId);
    }
  };

  const handleJoinRoomFrontDesk = () => {
    if (isFrontDesk && channelsFrontDesk.length && !channelDetailsFrontDesk && chatId) {
      joinRoomFrontDesk(chatId);
    }
  };

  const handleUpdatedScrollTop = () => {
    if (!scrollBlock.current) return;

    const limit = 10;
    const { offsetHeight } = scrollBlock.current;

    const scrollHeight = Math.round(Math.abs(scrollTop) + offsetHeight);
    if (scrollHeight + 1 <= scrollBlock.current.scrollHeight) return;

    const channelId = isFrontDesk ? channelDetailsFrontDesk?._id : channelDetails?._id;
    if (chatId !== channelId) return;

    const messagesAvailable = isFrontDesk ? dateMessagesFrontDesk?.length : dateMessages?.length;
    if (!messagesAvailable) return;

    const getMessage = isFrontDesk ? getOldMessageFrontDesk : getOldMessage;
    getMessage({ channelId, limit, skip }, () => setSkip(skip + 1));
  };

  const handleMarkSeen = () => {
    if (channelDetails && !isZoomCall) {
      const channel = channels.find((item) => item.channelId === channelDetails._id);
      if (channel?.unreadMessageCount) {
        markSeen({ channelId: channel.channelId, userId });
      }
    }
  };

  const handleMarkSeenFrontDesk = () => {
    if (channelDetailsFrontDesk) {
      const channel = channelsFrontDesk.find(
        (item) => item.channelId === channelDetailsFrontDesk._id
      );
      if (channel?.unreadMessageCount) {
        markSeenFrontDesk({ channelId: channelDetailsFrontDesk._id, userId });
      }
    }
  };

  useEffect(handleMarkSeen, [channels, channelDetails]);

  useEffect(handleMarkSeenFrontDesk, [channelsFrontDesk, channelDetailsFrontDesk]);

  useEffect(handleJoinRoom, [chatId, channels, channelDetails]);

  useEffect(handleJoinRoomFrontDesk, [chatId, channelsFrontDesk, channelDetailsFrontDesk]);

  useEffect(handleUpdatedScrollTop, [scrollTop, channelDetails, channelDetailsFrontDesk]);

  useEffect(() => {
    setSkip(1);
    closeChannel();
    closeChannelFrontDesk();
  }, [chatId]);

  const messages = isFrontDesk ? dateMessagesFrontDesk : dateMessages;
  const loading = isFrontDesk ? isLoadingFrontDesk : isLoadingMessages;

  return (
    <>
      <Loader isVisible={isLoading || ((loading || messages === null) && !!chatId)} />
      <div
        className={classNames(
          'flex flex-col overflow-hidden',
          isZoomCall ? 'h-full md:h-[calc(100%_-_87px)]' : 'h-full md:h-[calc(100%_-_80px)]'
        )}
        data-testid="chat_content"
      >
        <div
          className="relative flex flex-1 flex-col-reverse overflow-auto bg-gray-50 px-4 md:p-0"
          data-testid="messages_scroll_area"
          ref={scrollBlock}
          onScroll={handleScroll}
        >
          <div className="flex-1">
            {!loading && messages?.length === 0 && (
              <>
                {isFrontDesk ? (
                  <div className="mx-auto mt-8 w-fit rounded-lg bg-gray-100 px-[18px] py-10 md:bg-gray-50 md:p-10">
                    <Common.Illustration className="mx-auto max-w-[307px]" name="messages-empty" />
                    <p className="text-center text-gray-500 italic">
                      No requests have been submitted.
                    </p>
                  </div>
                ) : (
                  <>
                    <ChatDate date={new Date().toDateString()} />
                    <Message
                      combined={false}
                      key={'message._id'}
                      message={DEFAULT_MESSAGE}
                      mobile={!!isZoomCall}
                      self={false}
                    />
                  </>
                )}
              </>
            )}
            {messages?.map((el: DateMessagesProps) => (
              <FadeWrapper className="relative" key={el.date}>
                <ChatDate date={el.date} />
                {el.messages.map((message, index) => {
                  const combined = getCombinedMessageState(message, el.messages[index + 1]);

                  return (
                    <Message
                      combined={combined}
                      key={message._id}
                      message={message}
                      mobile={!!isZoomCall}
                      self={message.system || message.senderDetails?.id === userId}
                    />
                  );
                })}
              </FadeWrapper>
            ))}
          </div>
        </div>
        {!!chatId && (
          <div className="flex flex-col gap-4 bg-gray-100 pt-4 md:p-4">
            {!isZoomCall &&
              (data?.frontDescMessage ? (
                <Common.Alert
                  className="whitespace-normal max-md:mx-4 max-md:mt-4 max-md:text-mSm"
                  type="error"
                  colorableBackground
                  onClose={() => {}}
                >
                  {data.frontDescMessage}
                </Common.Alert>
              ) : (
                <p className="text-center text-mSm text-gray">
                  Our General support team usually responds in 1-2 days.
                </p>
              ))}
            <ChatForm
              attachments={activeDraft.attachments}
              disabledSubmit={disabledSubmit}
              onAddFiles={addDraftAttachments}
              onRemoveAttachment={removeDraftAttachment}
              onSubmit={() => {
                void onSubmit();
              }}
              onValueChange={handleDraftChange}
              value={activeDraft.message}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Chat;
